import Vue from 'vue'
import Vuetify from 'vuetify'
import ja from 'vuetify/src/locale/ja'
import en from 'vuetify/src/locale/en'
import th from 'vuetify/src/locale/th'
import { AxiosError } from 'axios'

import moment from 'moment'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'

import '@mdi/font/css/materialdesignicons.css' // Ensure you are using css-loader

import 'vuetify/dist/vuetify.min.css'
import { mask } from 'vue-the-mask'
import VueMeta from 'vue-meta'
import Component from 'vue-class-component'

import App from './App.vue'
import router from './router'
import store from './store'
import createAPI from './api'
import createAPIV3 from './api-v3'

import VirtualDataTable from './virtual-data-table'
import toast from './toast'
import i18n, { registerVuetify } from './locales'
import {
  getLanguage,
  getDateDisplayFormat,
  getDateTimeDisplayFormat,
} from './utils'
import { langOfPath, stripLang, withLang } from './i18n-route'

declare global {
  interface Window {
    Payjp: any
  }
}
Component.registerHooks(['metaInfo'])

Vue.use(VueMeta, {
  // keyName: 'metaInfo',
  // attribute: 'data-vue-meta',
  // ssrAttribute: 'data-vue-meta-server-rendered',
  // tagIDKeyName: 'vmid',
  refreshOnceOnNavigation: true,
})

Vue.directive('mask', mask)
Vue.config.productionTip = false
Vue.use(Vuetify)
Vue.use(VirtualDataTable)
Vue.use(toast)
const api = createAPI(() => store.getters.token)
const apiv3 = createAPIV3(() => store.getters.token)

Vue.prototype.$api = api
Vue.prototype.$apiV3 = apiv3

store.$api = api

router.beforeEach((to, from, next) => {
  // Public routes are declared with meta.public, so this check is unaffected
  // by the locale prefix and needs no update when a public route is added.
  if (!store.getters.loggedIn && !to.matched.some(r => r.meta.public))
    next(
      withLang(
        // stripLang: ?next= must never carry a locale, or the post-login
        // redirect would pin the user to the locale they had before logging in.
        { name: 'login', query: { next: stripLang(to.fullPath) } },
        // langOfPath before getLanguage(): on an unmatched (404-shaped) URL
        // params.lang is empty, and getLanguage()'s URL step reads
        // router.currentRoute - during beforeEach still the route being LEFT.
        // Logged out on a /th page, #/ja/typo would otherwise redirect to
        // /th/login while the URL and the UI both said ja. Same reasoning as
        // the lang guard's 404 branch in router.ts.
        (to.params.lang as string) || langOfPath(to.path) || getLanguage()
      )
    )
  else next()
})

function getValidationErrors(err: AxiosError) {
  // TODO: 401のtokenのexpireに対応
  let response = err.response
  if (response) {
    if (response.status === 400) {
      let data = response.data
      return data
    }
  }
  console.warn(err, response)
  return { nonFieldErrors: [i18n.t('common.message.unexpectedError')] }
}
Vue.prototype.$getValidationErrors = getValidationErrors

Vue.filter('date', (value: string | null, format?: string) => {
  if (!value) return value
  // Read i18n.locale, not getLanguage(): this registers a reactive dependency
  // on the rendering component, so the filter re-runs when the locale changes.
  const locale = i18n.locale
  const displayFormat = format || getDateDisplayFormat(locale)
  return moment(value)
    .locale(locale)
    .format(displayFormat)
})
Vue.filter('datetime', (value: string | null, format?: string) => {
  if (!value) return value
  const locale = i18n.locale
  const displayFormat = format || getDateTimeDisplayFormat(locale)
  return moment(value)
    .locale(locale)
    .format(displayFormat)
})
Vue.filter('time', (value: string | null, format = 'HH:mm') => {
  if (!value) return value
  return moment(value).format(format)
})
Vue.filter('jpy', (value: any) => {
  let num = 0
  if (typeof value === 'string') {
    num = parseInt(value)
  } else if (typeof value === 'number') {
    num = value
  } else {
    return value
  }
  if (isNaN(num)) return value
  return num.toLocaleString('ja-JP') + i18n.t('reservation.ticketForm.text.yen')
})
;(async () => {
  await store.dispatch('refresh')
  const vuetify = new Vuetify({
    lang: {
      locales: { ja, en, th },
      current: getLanguage(),
    },
  })
  registerVuetify(vuetify)
  new Vue({
    i18n,
    router,
    store,
    vuetify,
    render: h => h(App),
  }).$mount('#app')
})()
